import { useCallback, useState } from 'react';
import { gql, useApolloClient, useQuery } from '@apollo/client';

import { queryStrings } from '@trqk/resolvers';
import { removeNulls } from '@trqk/utils';

const { GET_SESSION_CURRENT_USER } = queryStrings;

const GET_TRQK_SONG_NAME_MATCHES_FOR_USER = gql`
    query getTrqkSongNameMatchesForUser(
        $id: ID!
        $entity_id: ID
        $statement_id: String
        $file_names: [String!]
    ) {
        getTrqkSongNameMatchesForUser(
            id: $id
            entity_id: $entity_id
            statement_id: $statement_id
            file_names: $file_names
        ) {
            file_name
            songs {
                id
                composer
                title
            }
        }
    }
`;

interface UseTrackNameMatchesInput {
    /**
     * @param error an error thrown by the `getTrqkSongNameMatchesForUser` query, if any occurs
     * @return undefined
     */
    onGetTrackNameMatchesError(error: Error): void;
}

interface UseTrackNameMatchesResult {
    /**
     * Set of {@link DropdownInputOption} IDs which should be disabled because they have already
     * been used to select a match.
     */
    disabledOptionIds: Set<Maybe<string>>;
    /**
     * Get the list of potential track name matches for each trqk audio to match. This is used to
     * update the returned `optionListsByFilename` list.
     */
    getTrackNameMatches: GetTrackNameMatches;
    /**
     * Callback that accepts a change in the DropdownInput selection state and updates the
     * `trqkSongUpdates` and `disabledOptionIds`.
     */
    handleDropdownInputChange: HandleMatchTracksDropdownInputChange;
    /**
     * Object where each key is a filename and each value is a list of {@link DropdownInputOption}
     * objects to be used for the DropdownInput that can select the statement work which matches
     * that filename.
     */
    optionListsByFilename: Record<string, DropdownInputOption[]>;
    /**
     * Array of {@link TrqkSongUpdate} objects, each of which supplies arguments for API calls that
     * can be made in order to add an audio file to a trqkSong from an existing statement.
     */
    trqkSongUpdates: TrqkSongUpdate[];
}

/**
 * Exposes methods for fetching a list of song name matches from the `getTrqkSongNameMatchesForUser`
 * query. Also exposes data for setting up {@link DropdownInput} components that can be used to
 * create combobox inputs for each filename in order to select a match.
 *
 * This hook is expected to be used a context where multiple audio files need to be matched to a
 * list of filenames. It returns a list of `disabledOptionIds` which can be used to enforce a limit
 * of one match per trqkAudioId.
 *
 * @param input - {@link UseTrackNameMatchesInput}
 * @returns a {@link UseTrackNameMatchesResult}
 */
export const useTrackNameMatches = ({
    onGetTrackNameMatchesError,
}: UseTrackNameMatchesInput): UseTrackNameMatchesResult => {
    const client = useApolloClient();
    const { data: currentUserData } = useQuery(GET_SESSION_CURRENT_USER);
    const [disabledOptionIds, setDisabledOptionIds] = useState<
        Set<Maybe<string>>
    >(new Set());
    const userId = currentUserData?.session?.currentUser?.id;

    const [optionListsByFilename, setOptionListsByFilename] = useState<
        Record<string, DropdownInputOption[]>
    >({});

    const [trqkSongUpdates, setTrqkSongUpdates] = useState<TrqkSongUpdate[]>(
        [],
    );

    const getTrackNameMatches = useCallback(
        ({ audiosToMatch, statementId }: GetTrackNameMatchesInput) => {
            if (userId && audiosToMatch.length > 0) {
                const fileNames = audiosToMatch.map(
                    ({ filename }: TrqkAudioToMatch): string => filename,
                );

                client
                    .query<{
                        getTrqkSongNameMatchesForUser: TrqkNameMatchSuggestion[];
                    }>({
                        query: GET_TRQK_SONG_NAME_MATCHES_FOR_USER,
                        variables: {
                            id: userId,
                            file_names: fileNames,
                            statement_id: statementId,
                        },
                    })
                    .then(result => {
                        const nameMatchData =
                            result?.data?.getTrqkSongNameMatchesForUser ?? null;

                        if (nameMatchData) {
                            const nextFilenameLists: Record<
                                string,
                                DropdownInputOption[]
                            > = {};

                            nameMatchData.forEach(
                                ({
                                    file_name: filename,
                                    songs,
                                }: TrqkNameMatchSuggestion): void => {
                                    nextFilenameLists[filename] =
                                        removeNulls<TrqkNameMatchSongReference>(
                                            songs,
                                        ).map(
                                            ({
                                                id,
                                                title,
                                            }: TrqkNameMatchSongReference): DropdownInputOption => ({
                                                id,
                                                label: title,
                                                value: {
                                                    id,
                                                    title,
                                                },
                                            }),
                                        );
                                },
                            );

                            setOptionListsByFilename(nextFilenameLists);

                            const defaultTrqkSongUpdates = audiosToMatch.reduce(
                                (
                                    acc: TrqkSongUpdateMap,
                                    { trqkAudioId, filename }: TrqkAudioToMatch,
                                ): TrqkSongUpdateMap => {
                                    const trqkSongId =
                                        nextFilenameLists?.[filename]?.[0]
                                            ?.value?.id ?? null;
                                    const title =
                                        nextFilenameLists?.[filename]?.[0]
                                            ?.value?.title ?? null;

                                    if (
                                        !acc.usedFilenames.includes(filename) &&
                                        typeof trqkSongId === 'string' &&
                                        typeof title === 'string'
                                    ) {
                                        acc.updates.push({
                                            trqkSongId,
                                            trqkAudioId,
                                            title,
                                        });
                                        acc.usedFilenames.push(filename);
                                    }

                                    return acc;
                                },
                                { updates: [], usedFilenames: [] },
                            ).updates;

                            setTrqkSongUpdates(defaultTrqkSongUpdates);

                            setDisabledOptionIds(
                                new Set(
                                    defaultTrqkSongUpdates.map(
                                        (update: TrqkSongUpdate): string =>
                                            update.trqkSongId,
                                    ),
                                ),
                            );
                        }
                    })
                    .catch(e => {
                        onGetTrackNameMatchesError(e as Error);
                    });
            }
        },
        [userId],
    );

    const handleDropdownInputChange = useCallback(
        ({
            trqkAudioId,
            option,
            prevOption,
        }: MatchTracksDropdownInputChange): void => {
            // When updating the list of trqkSong API requests to be made, only add one update per
            // trqkSongId.
            let nextTrqkSongUpdates = [...trqkSongUpdates];

            let foundExistingUpdate = false;

            for (const update of nextTrqkSongUpdates) {
                if (update.trqkSongId === option?.id) {
                    // If an existing update is found, update it in place
                    foundExistingUpdate = true;

                    const newTrqkAudioId = option?.value?.trqkAudioId ?? null;

                    /* eslint-disable no-param-reassign */
                    if (typeof newTrqkAudioId === 'string') {
                        update.trqkAudioId = newTrqkAudioId;
                    }
                    /* eslint-enable no-param-reassign */

                    break;
                }
            }

            if (!foundExistingUpdate && typeof option?.id === 'string') {
                nextTrqkSongUpdates.push({
                    trqkSongId: option?.id,
                    trqkAudioId,
                    title: option?.label ?? 'unknown track',
                });
            }

            // If the previously selected option is no longer used, remove it from the list of
            // updates
            nextTrqkSongUpdates = nextTrqkSongUpdates.filter(
                (currentUpdate: TrqkSongUpdate): boolean =>
                    currentUpdate.trqkSongId !== prevOption?.id,
            );

            setTrqkSongUpdates(nextTrqkSongUpdates);

            setDisabledOptionIds(
                new Set(
                    nextTrqkSongUpdates.map(
                        (update: TrqkSongUpdate): string => update.trqkSongId,
                    ),
                ),
            );
        },
        [],
    );

    return {
        disabledOptionIds,
        getTrackNameMatches,
        handleDropdownInputChange,
        optionListsByFilename,
        trqkSongUpdates,
    };
};
